import { useState } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import ConfigureCriteriaForm from './ConfigureCriteriaForm';
import OperationSelectList from './OperationSelectList';
import useVerificationConfigPageForm from '../../../hooks/useVerificationConfigPageForm';

const LoadingOverlay = ({ message }: { message: string }) => (
    <div className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {message}
        </div>
    </div>
);

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

const VerificationConfigPageForm = ({ onClose, onCreated }: Props) => {
    const {
        step, setStep, selected, search, setSearch, isLoading, isCreating,
        operationType, setOperationType, obligationType, setObligationType,
        traderName, setTraderName, traderResults, showTraderDropdown, isSearchingTrader,
        validationConfig, setValidationConfig,
        analysisCriteria, setAnalysisCriteria, sampledTitles, setSampledTitles,
        filteredOps, intensity,
        searchTrader, handleSelectTrader, handleSelectOperation, handleCreate,
    } = useVerificationConfigPageForm({ onClose, onCreated });

    const [assignorName, setAssignorName] = useState('');
    const [assignorTaxId, setAssignorTaxId] = useState('');

    const handleNext = () => {
        if (selected) {
            setAssignorName(selected.assignorName || '');
            setAssignorTaxId(selected.assignorCnpj || '');
            setStep('configure');
        }
    };

    // Step 1: Select operation
    if (step === 'select') {
        return (
            <div className="relative">
                {isLoading && <LoadingOverlay message="Carregando operações..." />}
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Nova Verificação de Recebíveis</h3>
                    <button onClick={handleNext} disabled={!selected}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-xs">Próximo</button>
                </div>

                <OperationSelectList
                    operations={filteredOps}
                    isLoading={isLoading}
                    selected={selected}
                    onSelect={handleSelectOperation}
                    showStatusFilter
                />
            </div>
        );
    }

    // Step 2: Configure analysis
    return (
        <div className="relative">
            {isCreating && <LoadingOverlay message="Aguarde, criando análise..." />}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Configurar Análise</h3>
                <div className="flex items-center gap-3">
                    <button onClick={() => setStep('select')} disabled={isCreating}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-xs disabled:opacity-50">Voltar</button>
                    <button onClick={handleCreate} disabled={isCreating}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs disabled:opacity-50">
                        {isCreating ? 'Criando...' : 'Criar Análise'}
                    </button>
                </div>
            </div>

            <ConfigureCriteriaForm
                operationSummary={{ operationCode: selected!.operationCode, assignorName: selected!.assignorName, totalAmount: selected!.totalAmount || 0, totalReceivables: selected!.totalReceivables || 0 }}
                operationType={operationType} setOperationType={setOperationType}
                obligationType={obligationType} setObligationType={setObligationType}
                traderName={traderName} setTraderName={setTraderName}
                assignorName={assignorName} setAssignorName={setAssignorName}
                assignorTaxId={assignorTaxId} setAssignorTaxId={setAssignorTaxId}
                validationConfig={validationConfig} setValidationConfig={setValidationConfig}
                analysisCriteria={analysisCriteria} setAnalysisCriteria={setAnalysisCriteria}
                sampledTitles={sampledTitles} setSampledTitles={setSampledTitles}
                disabled={isCreating}
            />

            {/* Open batch warning */}
            {selected && selected.status !== 'closed' && (
                <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-5">
                    <FiAlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                        <strong>Lote aberto:</strong> NFes podem entrar ou sair desta operação. Você poderá re-sincronizar quando o lote for fechado.
                    </div>
                </div>
            )}
        </div>
    );
};

export default VerificationConfigPageForm;
